import { FeatureFlag } from '@ps/web-core-enums';
import classNames from 'clsx';
import React, { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { useOnboardingContext } from '@swf/components/Onboarding/OnboardingContext/OnbordingContext';
import StarsButton from '@swf/components/StarsButton/StarsButton';
import useFeature from '@swf/core/hooks/useFeature';
import type { Dispatch } from '@swf/core/store';
import { select } from '@swf/core/store';
import {
  Navigation as BaseNavigation,
  type NavigationProperties,
  playAnimations
} from '@swf/poker';

import IconTV from '../../assets/icon-tv.animation.svg';
import { usePokerSpinAndGoWidget } from '../../model';
import { ModalRoute, OnboardingStep } from '../../types';
import type { GameTypeName } from '../../types/Game';

import { TXTCLI_Watch_Games } from './Navigation.messages.json';
import styles from './Navigation.scss';
import Ticker from './Ticker/Ticker';

const Navigation = ({
  navTabs = [],
  selectedTab,
  onSelectTab
}: NavigationProperties<GameTypeName>) => {
  const watchGamesVisibility = useFeature(FeatureFlag.PKRSpinAndGoWatchGamesVisibility);
  const { setFlowToStep, activeStep, onboardingFinished } = useOnboardingContext();
  const onboardingFocused = OnboardingStep.GameTypeSelection === activeStep;
  const { modal } = useDispatch<Dispatch>();
  const { widgetSelect, widgetId } = usePokerSpinAndGoWidget();
  const tickerMessage = useSelector(widgetSelect.tickerMessage);
  const isDesktopEmbedded = useSelector(select.device.isDesktopEmbedded);

  const handleWatchGamesOpen = useCallback(
    () =>
      modal.enqueue({
        path: ModalRoute.WatchGames,
        locationState: { activeTab: tickerMessage?.tabType, widgetId }
      }),
    [modal, tickerMessage, widgetId]
  );
  const handleHover = (event: React.MouseEvent<HTMLDivElement>) => {
    const leafAnimations = document.querySelectorAll(
      '.WGLeafAnimation'
    ) as NodeListOf<SVGAnimateElement>;
    leafAnimations.forEach((item) => item.beginElement());
    playAnimations(event);
  };

  return (
    <div
      className={classNames(
        isDesktopEmbedded ? styles.embeddedContainer : styles.navigationContainer,
        styles[`navigationBgr${selectedTab}`]
      )}
    >
      <div className={styles.navigationContent}>
        {watchGamesVisibility && (
          <div
            className={styles.watchGamesContent}
            onMouseOver={handleHover}
            onClick={handleWatchGamesOpen}
          >
            <Ticker />
            <IconTV />
            <StarsButton
              translation={TXTCLI_Watch_Games}
              variant="text"
              size="small"
              iconPosition="before"
              className={styles.watchGamesButton}
            />
          </div>
        )}
        <BaseNavigation
          navTabs={navTabs}
          selectedTab={selectedTab}
          onSelectTab={onSelectTab}
          infoRef={setFlowToStep ? setFlowToStep(OnboardingStep.GameTypeSelection) : null}
          className={classNames(styles.navigation, {
            [styles.onboardingFocus]: !onboardingFinished && onboardingFocused
          })}
        />
      </div>
    </div>
  );
};

export default Navigation;
